import { ThemeEvents } from './events.js';
import { Component } from './component.js';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductSizeRefs
 * @property {HTMLElement} sizeContainer
 */

/**
 * A custom element that displays the product size.
 * This component listens for variant update events and updates the size display accordingly.
 * Resolution order (computed server-side in snippets/product-size.liquid):
 * 1. product.metafields.custom.size.value
 * 2. selected_or_first_available_variant.metafields.custom.size.value
 * 3. nothing
 *
 * @extends {Component<ProductSizeRefs>}
 */
class ProductSize extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the size.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { sizeContainer } = this.refs;
        // Find the new product-size element in the updated HTML
        const newProductSize = html.querySelector(`product-size[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductSize) return;

        // Update size container
        const newSizeContainer = newProductSize.querySelector('[ref="sizeContainer"]');
        if (newSizeContainer && sizeContainer) {
          sizeContainer.replaceWith(newSizeContainer);
        }

        // Keep the wrapper's own visibility in sync with the freshly rendered markup,
        // so the accordion row collapses to nothing when there's no size on either
        // the product or the selected variant.
        this.style.display = newProductSize.style.display || '';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-size] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-size')) {
  customElements.define('product-size', ProductSize);
}
